import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { chatApi } from '../../api/chat';
import { communityApi } from '../../api/community';
import type { SocialUser } from '../../api/community.types';
import { useAsync } from '../../hooks/useAsync';
import { NAV_PATH } from '../../shell/nav';
import './social.css';

interface SocialValue {
  me: SocialUser | null;
  toast: (message: string) => void;
  /** "Nhắn tin" ở mini profile: mở/khởi tạo DM rồi chuyển sang trang Chat. */
  openDm: (peer: SocialUser) => Promise<void>;
  openingDm: boolean;
}

const SocialContext = createContext<SocialValue>({
  me: null,
  toast: () => undefined,
  openDm: async () => undefined,
  openingDm: false,
});

/** Đường dẫn trang Chat đang mở sẵn một hội thoại. */
export const chatPath = (conversationId: string) => `${NAV_PATH.chat}?c=${encodeURIComponent(conversationId)}`;

/** Bọc trang Cộng đồng/Chat: tài khoản của mình (để biết bài nào là của mình), toast của shell, mở DM. */
export function SocialProvider({ onToast, children }: { onToast: (message: string) => void; children: ReactNode }) {
  const navigate = useNavigate();
  const me = useAsync(() => communityApi.getMe(), []);
  const [openingDm, setOpeningDm] = useState(false);

  const openDm = useCallback(
    async (peer: SocialUser) => {
      setOpeningDm(true);
      try {
        const conversation = await chatApi.openDm(peer.id);
        navigate(chatPath(conversation.id));
      } catch {
        onToast('Không mở được cuộc trò chuyện, thử lại sau');
      } finally {
        setOpeningDm(false);
      }
    },
    [navigate, onToast],
  );

  const value = useMemo(() => ({ me: me.data, toast: onToast, openDm, openingDm }), [me.data, onToast, openDm, openingDm]);
  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
}

export function useSocial() {
  return useContext(SocialContext);
}
